import type { RouterNavigateOptions, To } from 'react-router-dom'
import { lazy, Suspense, useEffect, useRef, useState } from 'react'
import { AuthProvider } from '@/components/auth/AuthProvider'
import { UnlockMomentHost } from '@/components/engagement/UnlockMomentHost'
import { PublicRoot } from '@/components/layout/PublicRoot'
import { TauriTitleBarShell } from '@/components/layout/TauriTitleBarShell'
import { QueryCacheProvider } from '@/components/providers/QueryCacheProvider'
import { DeepLinkInterceptor } from '@/components/shared/DeepLinkInterceptor'
import { FieldHistoryShortcuts } from '@/components/shared/field-history/FieldHistoryControls'
import { RateImpactDialog } from '@/components/shared/RateImpactDialog'
import { RatePropagationNotifier } from '@/components/shared/RatePropagationNotifier'
import RealTimeInitializer from '@/components/shared/RealTimeInitializer'
import { WebVersionNotifier } from '@/components/shared/WebVersionNotifier'
import { StatusCommentPromptHost } from '@/components/tasks/status-comment/StatusCommentPromptHost'
import { TaskDescriptionSwitchGuard } from '@/components/tasks/task-sheet/TaskDescriptionSwitchGuard'
import { ThemeProvider } from '@/components/theme-provider'
import { TimeEntryProvider } from '@/components/time-entry'
import { Toaster } from '@/components/ui/sonner'
import { TooltipProvider } from '@/components/ui/tooltip'
import { EntityTerminologyProvider } from '@/contexts/EntityTerminologyContext'
import { LayoutProvider } from '@/contexts/LayoutContext'
import { SupplierTerminologyProvider } from '@/contexts/SupplierTerminologyContext'
import { TauriFileDropProvider } from '@/contexts/TauriFileDropContext'
import { TauriTitleBarProvider } from '@/contexts/TauriTitleBarContext'
import { useAnalyticsIdentity } from '@/hooks/useAnalyticsIdentity'
import { useAutoUpdater } from '@/hooks/useAutoUpdater'
import { useClearAppData } from '@/hooks/useClearAppData'
import { useDesktopAccountTransition } from '@/hooks/useDesktopAccountTransition'
import { useExternalLinks } from '@/hooks/useExternalLinks'
import { useOrganizationQueryInvalidation } from '@/hooks/useOrganizationQueryInvalidation'
import { usePageRestore, useRouteTracking } from '@/hooks/usePageRestoration'
import { PopupOrganizationInitializer } from '@/hooks/usePopupOrganizationInit'
import { usePreventBackspaceNavigation } from '@/hooks/usePreventBackspaceNavigation'
import { useRecordingUnloadWarning } from '@/hooks/useRecordingUnloadWarning'
import { useSwipeNavigation } from '@/hooks/useSwipeNavigation'
import { useTauriFocusRefetch } from '@/hooks/useTauriFocusRefetch'
import { useTauriModulePrefetch } from '@/hooks/useTauriModulePrefetch'
import { useTrackPageView } from '@/hooks/useTrackPageView'
import { useTrayNavigation } from '@/hooks/useTrayNavigation'
import { dismissBootSplash } from '@/lib/boot-splash'
import { prefetchPersonalAccountsFlag } from '@/lib/personal-accounts-flag'
import {
  appOnlyRoutes,
  publicBookingRoutes,
  publicEventRoutes,
  publicFormRoutes,
  publicKbRoutes,
  publicListShareRoutes,
  publicPayRoutes,
  publicProjectStatusRoutes,
  publicQuoteRoutes,
  publicRoadmapRoutes,
} from '@/lib/router'
import { SocketProvider } from '@/lib/socket/SocketContext'
import {
  initializePopupWindowStatus,
  isMainWindow,
  isPopupWindow,
  isTauri,
  restoreFocusWindows,
} from '@/lib/utils/tauri'
import { useAuth } from '@/store/auth.store'
import { useFieldHistoryStore } from '@/store/field-history.store'
import {
  QueryClient,
  QueryClientProvider,
  useQueryClient,
} from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { LazyMotion, MotionConfig } from 'framer-motion'
import {
  createBrowserRouter,
  Outlet,
  resolvePath,
  RouterProvider,
  useLocation,
  useRouteError,
} from 'react-router-dom'

import './styles/theme.css'

// Error boundary for handling chunk load failures after deployments
function ChunkErrorBoundary() {
  const error = useRouteError()

  // If boot fails into this boundary, release the desktop splash so the
  // error UI is visible instead of waiting on the 10s failsafe.
  useEffect(() => {
    dismissBootSplash()
  }, [])

  // Check if this is a chunk/module loading error (happens after new deployments)
  const isChunkError =
    error instanceof Error &&
    (error.message.includes('Failed to fetch dynamically imported module') ||
      error.message.includes('Loading chunk') ||
      error.message.includes('Loading CSS chunk') ||
      error.message.includes('Unable to preload CSS') ||
      error.message.includes('MIME type') ||
      error.message.includes('module script'))

  // Force a cache-busting reload
  const handleReload = () => {
    // Clear any caches we can
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => caches.delete(name))
      })
    }
    // Add cache-bust query param to force fresh fetch
    const url = new URL(window.location.href)
    url.searchParams.set('_cb', Date.now().toString())
    window.location.href = url.toString()
  }

  if (isChunkError) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-background">
        <div className="max-w-md px-4 text-center">
          <h1 className="mb-2 text-2xl font-semibold">App Updated</h1>
          <p className="mb-4 text-muted-foreground">
            A new version of the application is available. Please reload to
            continue.
          </p>
          <button
            onClick={handleReload}
            className="rounded-md bg-primary px-4 py-2 text-primary-foreground hover:bg-primary/90"
          >
            Reload page
          </button>
        </div>
      </div>
    )
  }

  // For other errors, show a generic error page
  return (
    <div className="flex h-screen w-full items-center justify-center bg-background">
      <div className="max-w-md px-4 text-center">
        <h1 className="mb-2 text-2xl font-semibold">Something went wrong</h1>
        <p className="mb-4 text-muted-foreground">
          {error instanceof Error
            ? error.message
            : 'An unexpected error occurred'}
        </p>
        <button
          onClick={handleReload}
          className="rounded-md bg-primary px-4 py-2 text-primary-foreground hover:bg-primary/90"
        >
          Reload page
        </button>
      </div>
    </div>
  )
}

// Scroll restoration component
function ScrollRestoration() {
  const location = useLocation()
  const [previousSearch, setPreviousSearch] = useState(location.search)

  useEffect(() => {
    const currentParams = new URLSearchParams(location.search)
    const previousParams = new URLSearchParams(previousSearch)

    // Check if only task parameter changed (added, removed, or modified)
    const currentParamsWithoutTask = new URLSearchParams(currentParams)
    currentParamsWithoutTask.delete('task')

    const previousParamsWithoutTask = new URLSearchParams(previousParams)
    previousParamsWithoutTask.delete('task')

    // If only the task parameter changed, skip scroll restoration
    if (
      currentParamsWithoutTask.toString() ===
      previousParamsWithoutTask.toString()
    ) {
      setPreviousSearch(location.search)
      return
    }

    // Update previous search for next comparison
    setPreviousSearch(location.search)

    // Use a small timeout to ensure all components have rendered
    const timeoutId = setTimeout(() => {
      // Scroll the main window to top
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' })

      // Also reset scroll for common scrollable containers
      const selectors = [
        'main',
        '[data-scroll-container]',
        '.overflow-auto',
        '.overflow-y-auto',
        '.overflow-scroll',
        '.overflow-y-scroll',
        '[role="main"]',
        '.main-content',
        '.page-content',
      ]

      selectors.forEach(selector => {
        const containers = document.querySelectorAll(selector)
        containers.forEach(container => {
          if (container instanceof HTMLElement) {
            container.scrollTo({ top: 0, left: 0, behavior: 'instant' })
          }
        })
      })
    }, 0)

    return () => clearTimeout(timeoutId)
  }, [location.pathname, location.search, location.hash, previousSearch])

  return null
}

// Component that initializes organization query invalidation
function OrganizationContextInitializer() {
  useOrganizationQueryInvalidation()
  return null
}

// Component that initializes auto-updater for desktop app
function AutoUpdaterInitializer() {
  useAutoUpdater()
  return null
}

// Component that handles external links in desktop app
function ExternalLinksHandler() {
  useExternalLinks()
  return null
}

// Component that handles clear app data menu event for desktop app
function ClearAppDataHandler() {
  useClearAppData()
  return null
}

function DesktopAccountTransitionHandler() {
  useDesktopAccountTransition()
  return null
}

// Component that prevents backspace from navigating back in desktop app
function BackspaceNavigationPreventer() {
  usePreventBackspaceNavigation()
  return null
}

// Component that maps two-finger trackpad swipes to back/forward navigation
// in the desktop app, the way Safari and Chrome do
function SwipeNavigationHandler() {
  useSwipeNavigation()
  return null
}

// Component that restores focus windows on app startup (desktop only, main window only)
function FocusWindowRestorer() {
  const hasRestored = useRef(false)

  useEffect(() => {
    // Only run once, only in Tauri, and only in the main window
    if (hasRestored.current || !isTauri() || isPopupWindow()) {
      return
    }

    const restore = async () => {
      // Check if this is the main window
      const isMain = await isMainWindow()
      if (!isMain) return

      hasRestored.current = true
      // Small delay to ensure main window is fully loaded
      await new Promise(resolve => setTimeout(resolve, 1000))
      await restoreFocusWindows()
    }

    restore()
  }, [])

  return null
}

// Component that tracks route changes and saves them for page restoration (desktop only, main window only)
function RouteTracker() {
  useRouteTracking()
  return null
}

// Component that restores the last visited page on app startup (desktop only, main window only)
function PageRestorer() {
  usePageRestore()
  return null
}

// Component that prefetches module chunks on desktop (Tauri only, main window only)
function TauriModulePrefetcher() {
  useTauriModulePrefetch()
  return null
}

// Bridges Tauri window focus into React Query's focusManager so
// refetchOnWindowFocus works on desktop (Tauri only, main window only)
function TauriFocusRefetchBridge() {
  useTauriFocusRefetch()
  return null
}

// Component that prefetches core module data after org is selected

// Warm the global personal-accounts rollout flag as soon as the user is
// authenticated, so the organization-setup wizard resolves it from cache
// instantly instead of firing the request only on page mount. Independent of
// org selection (the flag is checked without an org context).
function PersonalAccountsFlagPrefetcher() {
  const queryClient = useQueryClient()
  const isAuthenticated = useAuth(state => state.isAuthenticated)

  useEffect(() => {
    if (!isAuthenticated) return
    void prefetchPersonalAccountsFlag(queryClient)
  }, [isAuthenticated, queryClient])

  return null
}

// Component that handles navigation events from tray popover (desktop only, main window only)
function TrayNavigationHandler() {
  useTrayNavigation()
  return null
}

// Product analytics (PostHog + Sentry): sync identity and track page views
function AnalyticsBridge() {
  useAnalyticsIdentity()
  useTrackPageView()
  return null
}

function RecordingUnloadWarning() {
  useRecordingUnloadWarning()
  return null
}

// Fallback release for the desktop boot splash: OrgRouteGuard and the login
// page dismiss it at the right moment, but routes outside both (e.g.
// /organizations, client portal) would otherwise leave it to the 10s
// failsafe. Once the auth check settles, give the routed content a moment
// to land, then dismiss. Kept longer than the boot-critical path so the
// org-ready signal (which reveals the page in its final layout) wins on
// normal boots. No-op when the splash isn't active.
function BootSplashFallbackDismisser() {
  const isAuthLoading = useAuth(state => state.isLoading)

  useEffect(() => {
    if (isAuthLoading) return
    const timeoutId = setTimeout(() => dismissBootSplash(), 1200)
    return () => clearTimeout(timeoutId)
  }, [isAuthLoading])

  return null
}

// Root component that wraps the app with necessary providers
function Root() {
  const location = useLocation()
  const isClientPortalRoute = location.pathname.startsWith('/client-portal')
  const isBennyCompanionRoute = location.pathname === '/benny-companion'

  // Track popup window status with state to handle async initialization
  const [isPopup, setIsPopup] = useState(isPopupWindow())
  const hasInitialized = useRef(false)
  const isDesktopMainWindow = isTauri() && !isPopup
  const previousPathnameRef = useRef(location.pathname)

  // Initialize popup window status on mount (handles Tauri window label check)
  useEffect(() => {
    if (hasInitialized.current) return
    hasInitialized.current = true

    // For Tauri, do async check of window label
    if (isTauri()) {
      initializePopupWindowStatus().then(result => {
        if (result !== isPopup) {
          setIsPopup(result)
        }
      })
    }
  }, [isPopup])

  useEffect(() => {
    if (!isTauri()) return
    document.documentElement.setAttribute('data-tauri-window', 'true')
    return () => {
      document.documentElement.removeAttribute('data-tauri-window')
    }
  }, [])

  useEffect(() => {
    if (isBennyCompanionRoute) {
      document.documentElement.setAttribute('data-benny-companion', 'true')
      return () => {
        document.documentElement.removeAttribute('data-benny-companion')
      }
    }

    document.documentElement.removeAttribute('data-benny-companion')
    return undefined
  }, [isBennyCompanionRoute])

  useEffect(() => {
    if (previousPathnameRef.current === location.pathname) return
    previousPathnameRef.current = location.pathname
    useFieldHistoryStore.getState().clear()
  }, [location.pathname])

  return (
    <ThemeProvider defaultTheme="light" storageKey="scrambl-ui-theme">
      <QueryCacheProvider>
        <AuthProvider>
          <EntityTerminologyProvider>
            <SupplierTerminologyProvider>
              <LayoutProvider>
                <TauriTitleBarProvider>
                  <TauriFileDropProvider>
                    {!isClientPortalRoute && <OrganizationContextInitializer />}
                    {!isClientPortalRoute && <PopupOrganizationInitializer />}
                    <AnalyticsBridge />
                    <RecordingUnloadWarning />
                    <PersonalAccountsFlagPrefetcher />
                    <FieldHistoryShortcuts />
                    {isDesktopMainWindow && <BootSplashFallbackDismisser />}
                    {isDesktopMainWindow && <AutoUpdaterInitializer />}
                    <ExternalLinksHandler />
                    {isDesktopMainWindow && <ClearAppDataHandler />}
                    {isDesktopMainWindow && <DesktopAccountTransitionHandler />}
                    <BackspaceNavigationPreventer />
                    <SwipeNavigationHandler />
                    {isDesktopMainWindow && <FocusWindowRestorer />}
                    {isDesktopMainWindow && <RouteTracker />}
                    {isDesktopMainWindow && <PageRestorer />}
                    {isDesktopMainWindow && <TauriModulePrefetcher />}
                    {isDesktopMainWindow && <TauriFocusRefetchBridge />}
                    {isDesktopMainWindow && <TrayNavigationHandler />}
                    <SocketProvider>
                      {!isClientPortalRoute && <RealTimeInitializer />}
                      {isClientPortalRoute ? (
                        <>
                          <ScrollRestoration />
                          <TauriTitleBarShell>
                            <div className="bg-tauri-app-surface flex h-[var(--tauri-content-height,100vh)] min-h-0 w-full flex-col overflow-hidden">
                              <div className="route-transition-page min-h-0 flex-1">
                                <Outlet />
                              </div>
                            </div>
                          </TauriTitleBarShell>
                          <StatusCommentPromptHost />
                          <Toaster position="bottom-right" />
                          {import.meta.env.DEV && !isPopup && (
                            <ReactQueryDevtools
                              initialIsOpen={false}
                              // Bottom-left is the floating rail's utility
                              // island; keep the dev button clear of it.
                              buttonPosition="bottom-right"
                            />
                          )}
                        </>
                      ) : (
                        <TimeEntryProvider>
                          <ScrollRestoration />
                          {!isPopup && <RatePropagationNotifier />}
                          <RateImpactDialog />
                          <TaskDescriptionSwitchGuard />
                          <div
                            className={
                              isBennyCompanionRoute
                                ? 'flex h-full min-h-0 w-full flex-col overflow-hidden bg-transparent'
                                : 'bg-tauri-app-surface flex h-full min-h-0 w-full flex-col overflow-hidden'
                            }
                          >
                            <div className="route-transition-page min-h-0 flex-1">
                              <Outlet />
                            </div>
                          </div>
                          <StatusCommentPromptHost />
                          <Toaster position="bottom-right" />
                          <UnlockMomentHost />
                          {import.meta.env.DEV && !isPopup && (
                            <ReactQueryDevtools
                              initialIsOpen={false}
                              // Bottom-left is the floating rail's utility
                              // island; keep the dev button clear of it.
                              buttonPosition="bottom-right"
                            />
                          )}
                        </TimeEntryProvider>
                      )}
                    </SocketProvider>
                  </TauriFileDropProvider>
                </TauriTitleBarProvider>
              </LayoutProvider>
            </SupplierTerminologyProvider>
          </EntityTerminologyProvider>
        </AuthProvider>
      </QueryCacheProvider>
    </ThemeProvider>
  )
}

const EmptyHydrateFallback = () => null

// Dedicated React Query client for the companion window. Intentionally NOT
// the shared persisted client (QueryCacheProvider) — the companion is a
// separate webview and should not fight the main window over the persisted
// IndexedDB cache.
const companionQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 60_000,
    },
  },
})

function BennyCompanionRoot() {
  return (
    <ThemeProvider defaultTheme="light" storageKey="scrambl-ui-theme">
      <QueryClientProvider client={companionQueryClient}>
        {/* The timer/record panels reuse in-app pieces (LinkWorkSelector,
            LinkedModulePicker) that read entity/supplier terminology — mount
            the providers here too or those hooks throw. They only need the
            org store, settings query, and router, all present in this window. */}
        <EntityTerminologyProvider>
          <SupplierTerminologyProvider>
            <TooltipProvider delayDuration={0}>
              <Outlet />
              {/* Deliberately NO <Toaster /> here: the pet is a tiny always-on
                  transparent window, and timer/recording state already shows
                  on its own chips — toasts (started/stopped/etc.) just clutter
                  it. Without a Toaster mounted, toast.* calls harmlessly no-op
                  in this window. */}
            </TooltipProvider>
          </SupplierTerminologyProvider>
        </EntityTerminologyProvider>
      </QueryClientProvider>
    </ThemeProvider>
  )
}

// Lazy-loaded so the companion page stays out of the main bundle.
const BennyCompanionPage = lazy(() => import('@/pages/benny-companion'))

// Create router instance with separate route trees:
// 1. Public form routes use lightweight PublicRoot (no auth, sockets, etc.)
// 2. App routes use full Root with all providers
const router = createBrowserRouter([
  // Desktop companion route - local-only and independent of auth/org state.
  {
    element: <BennyCompanionRoot />,
    errorElement: <ChunkErrorBoundary />,
    HydrateFallback: EmptyHydrateFallback,
    children: [
      {
        path: '/benny-companion',
        element: (
          // Null fallback: the companion window is transparent, so any
          // visible loading UI would flash over the desktop.
          <Suspense fallback={null}>
            <BennyCompanionPage />
          </Suspense>
        ),
      },
    ],
  },
  // Public form routes - lightweight wrapper, no unnecessary services
  {
    element: <PublicRoot />,
    errorElement: <ChunkErrorBoundary />,
    HydrateFallback: EmptyHydrateFallback,
    children: [
      ...publicFormRoutes,
      ...publicEventRoutes,
      ...publicRoadmapRoutes,
      ...publicListShareRoutes,
      ...publicProjectStatusRoutes,
      ...publicBookingRoutes,
      ...publicKbRoutes,
      ...publicQuoteRoutes,
      ...publicPayRoutes,
    ],
  },
  // App routes - full providers stack
  {
    element: <Root />,
    errorElement: <ChunkErrorBoundary />,
    HydrateFallback: EmptyHydrateFallback,
    children: appOnlyRoutes,
  },
])

/* ── Route transitions on by default ──────────────────────────────────────
   React Router v7 has no router-level default for `viewTransition`, and this
   app has ~120 `<Link>`s and ~300 `useNavigate()` callers — far too many to
   opt in one call site at a time. For a data router both of those funnel
   through `router.navigate` (`useNavigateStable`, with `useLinkClickHandler`
   sitting on top of it), so wrapping the method once turns page transitions
   on everywhere. Call sites that pass `viewTransition` explicitly keep their
   own value.

   Note: popstate (browser and desktop back/forward, the two-finger swipe)
   does NOT come through here — React Router handles history pops internally,
   so those swap instantly. That matches native, where the back gesture owns
   its own animation. */
type RouterNavigate = typeof router.navigate
const baseNavigate = router.navigate.bind(router) as RouterNavigate

function shouldViewTransition(to: To | null): boolean {
  // Cheap opt-outs first: no support, or the user asked for stillness.
  if (typeof document.startViewTransition !== 'function') return false
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches)
    return false
  // A hidden document (background tab, minimized window) makes
  // startViewTransition reject with InvalidStateError, and React Router only
  // chains `.finally` on `transition.finished`, so that surfaces as an
  // unhandled rejection. Nothing is visible to animate anyway.
  if (document.visibilityState !== 'visible') return false

  const currentPathname = router.state.location.pathname

  try {
    // A `to` with no pathname of its own (`{ search }`, `null`) resolves back
    // to where we already are, so this covers those too.
    const target = resolvePath(to ?? '', currentPathname)
    // Same page, different query/hash — tab switches, `?comment=` deep links,
    // sheet params. Cross-fading there reads as a flicker, not a transition.
    return target.pathname !== currentPathname
  } catch {
    // Unresolvable `to` (rare): prefer the animation over an abrupt swap.
    return true
  }
}

// Cast: the wrapper deliberately collapses `navigate`'s two overloads
// (history delta vs. path) into one implementation, which TS can't infer back
// into the overloaded signature on its own.
router.navigate = ((to: To | null | number, opts?: RouterNavigateOptions) => {
  // History deltas take no options at all.
  if (typeof to === 'number') return baseNavigate(to)
  if (opts && 'viewTransition' in opts) return baseNavigate(to, opts)
  return baseNavigate(to, { ...opts, viewTransition: shouldViewTransition(to) })
}) as RouterNavigate

/* framer-motion ships its full feature set with every `motion` import, so the
   app imports `m` (the lazy proxy) instead and loads the features once, here.
   `domMax` rather than `domAnimation` because ~8 files use layout animations
   and ~7 use drag. No `strict`: a stray `motion.*` import still works, it just
   pulls its own bundle. */
const loadMotionFeatures = () => import('framer-motion').then(mod => mod.domMax)

function App() {
  return (
    <DeepLinkInterceptor>
      <WebVersionNotifier />
      <LazyMotion features={loadMotionFeatures}>
        <MotionConfig reducedMotion="user">
          <RouterProvider router={router} />
        </MotionConfig>
      </LazyMotion>
    </DeepLinkInterceptor>
  )
}

export default App
